/**
 * Charter-compliant error attributes extracted from any thrown value.
 * Always carries `error.kind` and `error.message`; the HTTP/code fields are
 * present only when the source error exposes them.
 */
export type ErrorAttributes = {
  'error.kind': string;
  'error.message': string;
  'http.status_code'?: number;
  'error.code'?: string;
};

/**
 * Error kinds whose `message` quotes a third party and must never reach observability.
 * Matched by `name`, which survives minification, unlike the class identifier.
 */
const REDACTED_MESSAGE_KINDS = ['ApiError', 'RetryInvoiceError'];

/**
 * Extracts observability attributes from any thrown value.
 *
 * Reads from properties that survive Terser minification in CRA prod builds:
 * - `error.name`            — `AxiosError` sets it explicitly (axios v1);
 *                             `ApiError` sets it via Object.assign.
 * - `error.status`          — present on ApiError.
 * - `error.response.status` — present on AxiosError.
 * - `error.code`            — present on ApiError (semantic backend code).
 *
 * Never relies on `error.constructor.name`: class identifiers are mangled in
 * production builds (e.g. `AxiosError` → `'o'`).
 *
 * @param error - Any value caught in a try/catch
 * @returns Attributes ready to spread into a logger payload
 */
export const extractErrorAttributes = (error: unknown): ErrorAttributes => {
  if (!(error instanceof Error)) {
    return { 'error.kind': 'Unknown', 'error.message': String(error) };
  }

  const status =
    (error as { status?: number }).status ?? (error as { response?: { status?: number } }).response?.status;

  const code = (error as { code?: string }).code;

  const kind = error.name && error.name.length > 0 ? error.name : 'Error';

  // Messages that originate outside this app may carry PII (full RFC, etc.) — Charter §5.1
  // forbids logging them. `ApiError` quotes the backend, and `RetryInvoiceError` quotes the PAC;
  // for both, the semantic fields (`error.code`, the caller's own attributes) are what
  // observability should rely on instead.
  const message = REDACTED_MESSAGE_KINDS.includes(kind) ? '[redacted]' : error.message;

  return {
    'error.kind': kind,
    'error.message': message,
    ...(typeof status === 'number' ? { 'http.status_code': status } : {}),
    ...(code ? { 'error.code': code } : {}),
  };
};
